import { SummaryData, Brand, Creator, Post, RawPost, Platform } from '../types/socialGlass';
import { PLATFORM_PATTERN } from '../constants/platforms';

export const transformTopBrands = (summaryData: SummaryData | null): Brand[] => {
  if (!summaryData?.TopBrandsAndTitles) return [];
  return summaryData.TopBrandsAndTitles.map((brand) => ({
    name: brand.brandName,
    mentions: brand.mentions,
    icon: "/PFPplaceholder.svg",
  }));
};

export const transformTopCreators = (summaryData: SummaryData | null): Creator[] => {
  if (!summaryData?.TopCreators) return [];
  
  return summaryData.TopCreators.map((creator) => {
    const platformMatch = creator.creatorRelatedSubmittedPosts?.[0]?.postUrl?.match(PLATFORM_PATTERN);
    const platform = (platformMatch?.[1] as Platform) || 'tiktok';
    
    return {
      name: creator.name || "Missing",
      followers: creator.followers,
      likes: creator.likes,
      mentions: creator.mentions || "Missing",
      platform,
      image: creator.image ? creator.image : "Missing",
      avatar: creator.pfp && creator.pfp !== "None" ? creator.pfp : "/placeholderpfp.jpg",
    };
  });
};

export const transformPosts = (summaryData: SummaryData | null, category: string): Post[] => {
  if (!summaryData?.TopPosts?.[category]) return [];
  
  return summaryData.TopPosts[category].HottestPosts.map((post: RawPost) => ({
    postId: String(post.postId),
    user: {
      name: post.postUsername,
      avatar: "/placeholderpfp.jpg", // Default avatar since pfp is not in this structure
    },
    video: post.postUrl,
    text: post.postDesc,
    platform: "/tiktok.svg",
    postLikes: post.likes,
    commentsCount: post.commentsCount,
    comments: post.topComments.map((comment) => ({
      user: {
        name: comment.username,
        avatar: "/placeholderpfp.jpg", // Default avatar since pfp is not in this structure
      },
      text: comment.commentText,
      likes: comment.likes,
    })),
  }));
};

export const transformBreakoutPosts = (summaryData: SummaryData | null, category: string): Post[] => {
  if (!summaryData?.TopPosts?.[category]) return [];
  
  return summaryData.TopPosts[category].BreakoutContent.map((post: RawPost) => ({
    postId: String(post.postId),
    user: {
      name: post.postUsername,
      avatar: "/placeholderpfp.jpg", // Default avatar since pfp is not in this structure
    },
    video: post.postUrl,
    text: post.postDesc,
    platform: "/tiktok.svg",
    postLikes: post.likes,
    commentsCount: post.commentsCount,
    comments: post.topComments.map((comment) => ({
      user: {
        name: comment.username,
        avatar: "/placeholderpfp.jpg", // Default avatar since pfp is not in this structure
      },
      text: comment.commentText,
      likes: comment.likes,
    })),
  }));
};

export const transformBrandSentiments = (summaryData: SummaryData | null): Record<string, string> => {
  if (!summaryData?.TopBrandsAndTitles) return {};
  
  return summaryData.TopBrandsAndTitles.reduce((acc, brand) => {
    acc[brand.brandName] = brand.overallBrandSentiment;
    return acc;
  }, {} as Record<string, string>);
};

export const transformCreatorSentiments = (summaryData: SummaryData | null): Record<string, string> => {
  if (!summaryData?.TopCreators) return {};
  
  return summaryData.TopCreators.reduce((acc, creator) => {
    acc[creator.name] = creator.overallCreatorSentiment;
    return acc;
  }, {} as Record<string, string>);
};

export const formatCount = (num: number): string => {
  const digitLength = Math.floor(Math.log10(num)) + 1;

  if (digitLength <= 4) {
    return `~${num.toLocaleString()}`;
  } else if (num < 1000000) {
    return `~${(num / 1000).toFixed(1).replace(/\.0$/, "")}k`;
  } else {
    return `~${(num / 1000000).toFixed(1).replace(/\.0$/, "")}M`;
  }
};

export const getBrandPosts = (summaryData: SummaryData | null, brandName: string): Post[] => {
  if (!summaryData?.TopBrandsAndTitles) return [];
  
  const brand = summaryData.TopBrandsAndTitles.find((b) => b.brandName === brandName);
  return brand
    ? brand.brandRelatedPosts.map((post: RawPost) => ({
        postId: String(post.postId || post.id || ''),
        user: {
          name: post.postUsername,
          avatar: "/placeholderpfp.jpg",
        },
        video: post.postUrl,
        text: post.postDesc,
        platform: "/tiktok.svg",
        postLikes: post.likes,
        commentsCount: post.commentsCount,
        comments: post.topComments.map((comment) => ({
          user: {
            name: comment.username,
            avatar: "/placeholderpfp.jpg",
          },
          text: comment.commentText,
          likes: comment.likes,
        })),
        postUsername: post.postUsername,
        postDesc: post.postDesc,
        topComments: post.topComments,
      }))
    : [];
};

export const getBrandComments = (summaryData: SummaryData | null, brandName: string) => {
  if (!summaryData?.TopBrandsAndTitles) return [];
  
  const brand = summaryData.TopBrandsAndTitles.find((b) => b.brandName === brandName);
  return brand ? brand.brandRelatedComments : [];
};

export const getCreatorPosts = (summaryData: SummaryData | null, creatorName: string): Post[] => {
  if (!summaryData?.TopCreators) return [];
  
  const creator = summaryData.TopCreators.find((c) => c.name === creatorName);
  return creator
    ? creator.creatorRelatedSubmittedPosts?.map((post: RawPost) => ({
        postId: String(post.postId || post.id || ''),
        user: {
          name: post.postUsername,
          avatar: "/placeholderpfp.jpg",
        },
        video: post.postUrl,
        text: post.postDesc,
        platform: "/tiktok.svg",
        postLikes: post.likes,
        commentsCount: post.commentsCount,
        comments: post.topComments.map((comment) => ({
          user: {
            name: comment.username,
            avatar: "/placeholderpfp.jpg",
          },
          text: comment.commentText,
          likes: comment.likes,
        })),
        postUsername: post.postUsername,
        postDesc: post.postDesc,
        topComments: post.topComments,
      })) || []
    : [];
};
